<template>
  <div class="disable-autopayments-modal">
    <div class="disable-autopayments-modal__content">
      <div v-if="!isLoading && !result" class="disable-autopayments-modal__form">
        <h2 class="disable-autopayments-modal__title">Отключение автосписания</h2>
        <p class="disable-autopayments-modal__message">
          Вы действительно хотите отключить автосписание?
        </p>
      </div>

      <!-- Состояние загрузки -->
      <div v-else-if="isLoading" class="disable-autopayments-modal__loading">
        <Loader />
        <p>Отключаем автосписание...</p>
      </div>

      <!-- Результат -->
      <div v-else-if="result" class="disable-autopayments-modal__result">
        <div v-if="result.success" class="disable-autopayments-modal__success">
          <h2 class="disable-autopayments-modal__title">Успешно!</h2>
          <p class="disable-autopayments-modal__message">
            Автосписание успешно отключено
          </p>
        </div>
        <div v-else class="disable-autopayments-modal__error">
          <h2 class="disable-autopayments-modal__title">Ошибка</h2>
          <p class="disable-autopayments-modal__message">
            {{ result.error }}
          </p>
          <p v-if="result.statusCode" class="disable-autopayments-modal__status-code">
            Код ошибки: {{ result.statusCode }}
          </p>
        </div>
      </div>
    </div>

    <div class="disable-autopayments-modal__footer">
      <div v-if="!isLoading && !result" class="disable-autopayments-modal__buttons">
        <button 
          class="disable-autopayments-modal__button disable-autopayments-modal__button--cancel"
          @click="$emit('close')"
        >
          Нет
        </button>
        <button 
          class="disable-autopayments-modal__button disable-autopayments-modal__button--confirm"
          @click="handleDisableAutopayments"
        >
          Да
        </button>
      </div>
      
      <div v-else-if="result" class="disable-autopayments-modal__result-buttons">
        <button 
          class="disable-autopayments-modal__button disable-autopayments-modal__button--ok"
          @click="$emit('close')"
        >
          OK
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { disableAutopaymentsRepository } from '@/shared/repositories/DisableAuthopayments'
import Loader from '@/ui/components/Loader.vue'

const emit = defineEmits<{
  close: []
}>()

const isLoading = ref(false)
const result = ref<{
  success: boolean
  error?: string
  statusCode?: number
} | null>(null)

const handleDisableAutopayments = async () => {
  isLoading.value = true
  result.value = null

  try {
    const response = await disableAutopaymentsRepository.disableAutopayments()
    
    if (response.status === 200 && 'success' in response.data) {
      result.value = { success: true }
    } else {
      result.value = {
        success: false,
        error: 'Неизвестная ошибка',
        statusCode: response.status
      }
    }
  } catch (error: any) {
    console.error('Ошибка при отключении автоплатежей:', error)
    
    let errorMessage = 'Произошла ошибка при отключении автосписания'
    let statusCode: number | undefined
    
    if (error.status) {
      statusCode = error.status
      if (error.data && 'detail' in error.data) {
        errorMessage = error.data.detail
      } else if (error.message) {
        errorMessage = error.message
      }
    }
    
    result.value = {
      success: false,
      error: errorMessage,
      statusCode
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/mixins.scss';

.disable-autopayments-modal {
  display: flex;
  flex-direction: column;
  text-align: center;

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-top: 20px;
  }

  &__form,
  &__loading,
  &__result {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__footer {
    margin-top: 24px;
    border-top: 1px solid $secondary-200;
    padding-top: 16px;
  }

  &__title {
    @include h2;
    color: $neutral-100;
    margin-bottom: 16px;
  }

  &__message {
    @include p1-regular;
    color: $neutral-100;
    margin-bottom: 24px;
    line-height: 1.5;
  }

  &__status-code {
    @include p3-regular;
    color: $neutral-60;
    margin-bottom: 24px;
  }

  &__loading {
    gap: 16px;
    color: $neutral-100;
    
    p {
      @include p1-regular;
    }
  }

  &__buttons,
  &__result-buttons {
    display: flex;
    gap: 12px;
    width: 100%;
  }

  &__result-buttons {
    justify-content: center;
  }

  &__button {
    flex: 1;
    height: 48px;
    border-radius: 12px;
    border: none;
    font-family: 'Unbounded', sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 18px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover:not(:disabled) {
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
    
    &--cancel {
      background: $secondary-200;
      color: $neutral-100;
      
      &:hover {
        background: $secondary-200-active;
      }
    }
    
    &--confirm {
      background: #ff4444;
      color: $neutral-100;
      
      &:hover {
        background: #e03333;
      }
    }

    &--ok {
      background: $primary-gradient;
      color: $neutral-200;
      
      &:hover {
        background: $primary-gradient-active;
      }
    }
  }

  &__success {
    .disable-autopayments-modal__title {
      color: #28a745;
    }
  }

  &__error {
    .disable-autopayments-modal__title {
      color: #ff4444;
    }
  }
}
</style>
